import React, { useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { useAuth } from '@/lib/auth';
import { FileText, FileSpreadsheet, Gem, Layers, ArrowRight, ScrollText, CalendarDays, CalendarClock, Sparkles, Circle, Coins, Component, Cpu, LayoutGrid } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';

export default function Reports() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Reports" subtitle="Generate downloadable PDF and Excel reports with your company branding.">
      <CompanyGate>
        <ReportsContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function ReportsContent({ companyId }) {
  const { hasPermission, hasMaterialAccess } = useAuth();
  // Wave 3.99l — Per-report visibility gates. Legacy ``reports.view`` is
  // expanded via ``LEGACY_ALIASES`` so existing users keep seeing every
  // tile until Super Admin explicitly toggles the sub-keys off. The
  // material tiles also honour ``materials.<mat>.see`` so a Diamond
  // Buyer never sees Colour Stone / Pearl closing balances even if the
  // sub-key is set.
  const canSeeUnified   = hasPermission('reports.unified_closing.view');
  const canSeeMemos     = hasPermission('reports.memos.view');
  // Customer Gold Report — company-wide advance gold & net balances across
  // customers. Gated by its own dedicated report permission (independent of
  // the per-customer gold ledger dialog on the Customers page).
  const canSeeCustomerGold = hasPermission('reports.customer_gold.view');
  // Wave 4.07 — CAD Development report.
  const canSeeCad = hasPermission('reports.cad.view');
  const canSeeProductionDigest = hasPermission('reports.production_digest.view');
  const canSeeEstimatedDelivery = hasPermission('reports.estimated_delivery.view');
  const canSeeDiamondCB = hasPermission('reports.diamond_closing.view') && hasMaterialAccess('diamond', 'see');
  const canSeeCsCB      = hasPermission('reports.color_stone_closing.view') && hasMaterialAccess('color_stone', 'see');
  const canSeePearlCB   = hasPermission('reports.pearl_closing.view') && hasMaterialAccess('pearl', 'see');
  const canSeeGoldRep   = hasPermission('reports.gold_ledger.view') && hasMaterialAccess('gold', 'see');
  // Wave 3.99aa — Mountings closing balance card visibility. Gated by
  // both the per-report ``view`` permission AND the mountings material
  // scope so a user without any Mountings visibility never sees the tile.
  const canSeeMountingsCB = hasPermission('reports.mountings_closing.view') && hasMaterialAccess('mountings', 'see');
  // Print gates — export buttons hide unless the user also has the
  // matching ``.print`` right (legacy ``reports.export`` still expands
  // to all six via ``LEGACY_ALIASES`` so nothing breaks on redeploy).
  const canPrintUnified   = hasPermission('reports.unified_closing.print');
  const canPrintDiamondCB = hasPermission('reports.diamond_closing.print') && hasMaterialAccess('diamond', 'see');
  const canPrintCsCB      = hasPermission('reports.color_stone_closing.print') && hasMaterialAccess('color_stone', 'see');
  const canPrintPearlCB   = hasPermission('reports.pearl_closing.print') && hasMaterialAccess('pearl', 'see');
  const canPrintGoldRep   = hasPermission('reports.gold_ledger.print') && hasMaterialAccess('gold', 'see');
  // Wave 3.99aa
  const canPrintMountingsCB = hasPermission('reports.mountings_closing.print') && hasMaterialAccess('mountings', 'see');
  const [pdfDlg, setPdfDlg] = useState({ open: false, report: null, kind: 'pdf' });
  // Phase 19 / Wave 3.14 — Unified Closing Stock supports an as-on date.
  // Default to today (YYYY-MM-DD).
  const todayIso = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  const [asOfDate, setAsOfDate] = useState(todayIso);
  // Human-friendly label used in dialog titles + filenames.
  const formatAsOfLabel = (iso) => {
    if (!iso) return '';
    try {
      const [y, , d] = iso.split('-');
      const month = new Date(`${iso}T00:00:00`).toLocaleString('en-GB', { month: 'short' });
      return `${d} ${month} ${y}`;
    } catch {
      return iso;
    }
  };

  const reports = [
    {
      id: 'closing-diamonds',
      icon: Gem,
      palette: 'var(--diamond-bg)',
      icon_color: 'var(--diamond-icon)',
      title: 'Diamond Closing Balance',
      description: 'Tree of classification → packet with carat, pieces, and value subtotals plus grand total.',
      pdf_path: '/reports/closing-balance/diamonds/pdf',
      excel_path: '/reports/closing-balance/diamonds/excel',
      pdf_name: 'diamond_closing_balance.pdf',
      excel_name: 'diamond_closing_balance.xlsx',
      visible: canSeeDiamondCB,
      printable: canPrintDiamondCB,
      testid: 'report-card-closing-diamonds',
    },
    {
      id: 'closing-color-stones',
      icon: Sparkles,
      palette: 'var(--colorstone-bg, #FCE7F3)',
      icon_color: 'var(--colorstone-icon, #C84A6F)',
      title: 'Colour Stone Closing Balance',
      description: 'Classification-wise closing stock of colour stones with carat, pieces, and value totals.',
      pdf_path: '/reports/closing-balance/color-stones/pdf',
      excel_path: '/reports/closing-balance/color-stones/excel',
      pdf_name: 'color_stone_closing_balance.pdf',
      excel_name: 'color_stone_closing_balance.xlsx',
      visible: canSeeCsCB,
      printable: canPrintCsCB,
      testid: 'report-card-closing-color-stones',
    },
    {
      id: 'closing-pearls',
      icon: Circle,
      palette: '#F1F5F9',
      icon_color: 'var(--pearl-icon, #6B7280)',
      title: 'Pearl Closing Balance',
      description: 'Classification-wise closing stock of pearls with carat, pieces, and value totals.',
      pdf_path: '/reports/closing-balance/pearls/pdf',
      excel_path: '/reports/closing-balance/pearls/excel',
      pdf_name: 'pearl_closing_balance.pdf',
      excel_name: 'pearl_closing_balance.xlsx',
      visible: canSeePearlCB,
      printable: canPrintPearlCB,
      testid: 'report-card-closing-pearls',
    },
    {
      id: 'gold-ledger',
      icon: Coins,
      palette: 'var(--gold-bg, #FEF3C7)',
      icon_color: 'var(--gold-icon)',
      title: 'Gold Ledger',
      description: 'Full 999 gold ledger with receipts, issues, adjustments, and closing purity-weighted stock.',
      pdf_path: '/reports/ledger/gold/pdf',
      excel_path: '/reports/ledger/gold/excel',
      pdf_name: 'gold_ledger.pdf',
      excel_name: 'gold_ledger.xlsx',
      visible: canSeeGoldRep,
      printable: canPrintGoldRep,
      testid: 'report-card-gold-ledger',
    },
    // Wave 3.99aa — Mountings Closing Balance tile. Excel export is
    // NOT provided (mountings are grams-only and light enough that PDF
    // suffices); the card intentionally hides the Excel affordance by
    // omitting ``excel_path``.
    {
      id: 'closing-mountings',
      icon: Component,
      palette: 'var(--gold-bg, #FEF3C7)',
      icon_color: 'var(--accent-warm, #B45309)',
      title: 'Mountings Closing Balance',
      description: 'Per-item semi-finished gold pieces closing stock with live gold-rate valuation (grams × purity × 999 rate).',
      pdf_path: '/reports/closing-balance/mountings/pdf',
      pdf_name: 'mountings_closing_balance.pdf',
      visible: canSeeMountingsCB,
      printable: canPrintMountingsCB,
      testid: 'report-card-closing-mountings',
    },
  ];
  const visibleReports = reports.filter((r) => r.visible);
  const anyTileVisible = canSeeUnified || canSeeMemos || canSeeCustomerGold || canSeeCad || visibleReports.length > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {!anyTileVisible && (
        <div
          className="md:col-span-2 text-sm text-slate-500 py-10 text-center border border-dashed rounded-md"
          data-testid="reports-no-access"
        >
          You don&apos;t have access to any reports. Ask your admin to grant report visibility.
        </div>
      )}
      {/* Phase 12 / Wave 3.14 — Unified Closing Stock tile with as-on date picker */}
      {canSeeUnified && (
      <Card className="p-5 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="report-card-unified-closing-balance">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-700">
            <Layers size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-[var(--ink-navy)]">Unified Closing Stock</div>
              <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">As-on Date</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Stock In Hand + On Approval + Work In Progress — a single consolidated view across Gold, Diamonds, Colour Stones &amp; Pearls. Pick an as-on date to view historical balances.
            </div>

            {/* As-on date selector */}
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="unified-as-of-date" className="text-[11px] text-slate-600 flex items-center gap-1 mb-1">
                  <CalendarDays size={12} /> Report as on
                </Label>
                <DatePicker
                  value={asOfDate}
                  maxDate={todayIso}
                  onChange={(e) => setAsOfDate(e.target.value || todayIso)}
                  className="h-9 w-44"
                  data-testid="unified-as-of-date-input"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-slate-500 hover:text-emerald-700"
                onClick={() => setAsOfDate(todayIso)}
                data-testid="unified-as-of-today-button"
              >
                Reset to today
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Button asChild className="luxury-button" data-testid="report-open-unified">
                <Link to={`/reports/unified-closing-balance?date=${encodeURIComponent(asOfDate || todayIso)}`}>
                  Open Report <ArrowRight size={14} className="ml-1.5" />
                </Link>
              </Button>
              {canPrintUnified && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setPdfDlg({ open: true, report: {
                      id: 'unified-closing-balance',
                      title: `Unified Closing Stock as on ${formatAsOfLabel(asOfDate || todayIso)}`,
                      pdf_path: '/reports/closing-balance/unified/pdf',
                      pdf_name: `unified_closing_stock_${(asOfDate || todayIso)}.pdf`,
                      excel_path: '/reports/closing-balance/unified/excel',
                      excel_name: `unified_closing_stock_${(asOfDate || todayIso)}.xlsx`,
                      as_of_date: asOfDate || todayIso,
                    }, kind: 'pdf' })}
                    data-testid="report-pdf-unified-closing-balance"
                  >
                    <FileText size={14} className="mr-1.5" /> Export PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPdfDlg({ open: true, report: {
                      id: 'unified-closing-balance',
                      title: `Unified Closing Stock as on ${formatAsOfLabel(asOfDate || todayIso)}`,
                      pdf_path: '/reports/closing-balance/unified/pdf',
                      pdf_name: `unified_closing_stock_${(asOfDate || todayIso)}.pdf`,
                      excel_path: '/reports/closing-balance/unified/excel',
                      excel_name: `unified_closing_stock_${(asOfDate || todayIso)}.xlsx`,
                      as_of_date: asOfDate || todayIso,
                    }, kind: 'excel' })}
                    data-testid="report-excel-unified-closing-balance"
                  >
                    <FileSpreadsheet size={14} className="mr-1.5" /> Export Excel
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Phase 15.7 — Memos Browser highlight card */}
      {canSeeMemos && (
      <Card className="p-5 border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="report-card-memos">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100 text-slate-700">
            <ScrollText size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-[var(--ink-navy)]">Issue &amp; Return Memos</div>
              <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">Ledger</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Browse every Issue and Return memo across goldsmiths and vendors. Filter by period, party, material, or memo type and jump straight to the printable PDF.
            </div>
            <div className="flex gap-2 mt-4">
              <Button asChild className="luxury-button" data-testid="report-open-memos">
                <Link to="/reports/memos">Open Memos <ArrowRight size={14} className="ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Customer Gold Ledger Report — gold received from / issued to customers */}
      {canSeeCustomerGold && (
      <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="report-card-customer-gold-ledger">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[var(--gold-bg)] text-[var(--gold-icon)]">
            <Coins size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-[var(--ink-navy)]">Customer Gold Ledger</div>
              <span className="text-[10px] uppercase tracking-wide bg-[var(--gold-bg)] text-[var(--gold-icon)] px-1.5 py-0.5 rounded">Grouped</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Gold received from and issued to end customers, grouped by customer with per-customer subtotals and grand totals. Filter by period, customer, and type.
            </div>
            <div className="flex gap-2 mt-4">
              <Button asChild className="luxury-button" data-testid="report-open-customer-gold-ledger">
                <Link to="/reports/customer-gold-ledger">Open Report <ArrowRight size={14} className="ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Wave 4.07 — CAD Development report tile. */}
      {canSeeCad && (
      <Card className="p-5 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="report-card-cad-development">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-700">
            <Cpu size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-[var(--ink-navy)]">CAD Development</div>
              <span className="text-[10px] uppercase tracking-wide bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">Filterable</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Every order whose CAD is developed — attributed to a CAD Developer, with CAD weight and date. Filter by period, CAD developer, page no, order no, and customer.
            </div>
            <div className="flex gap-2 mt-4">
              <Button asChild className="luxury-button" data-testid="report-open-cad-development">
                <Link to="/reports/cad-development">Open Report <ArrowRight size={14} className="ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Production Stage Digest report tile. */}
      {canSeeProductionDigest && (
      <Card className="p-5 border-sky-200 bg-gradient-to-br from-sky-50 to-white" data-testid="report-card-production-digest">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-sky-100 text-sky-700">
            <LayoutGrid size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-[var(--ink-navy)]">Production Stage Digest</div>
              <span className="text-[10px] uppercase tracking-wide bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded">Pivot</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              A live pivot of every active order form across each production stage — CAD, Ghat, Raw Material, Setting, QC and Delivery — grouped Customer-wise or Goldsmith-wise. Click any number to open the order forms.
            </div>
            <div className="flex gap-2 mt-4">
              <Button asChild className="luxury-button" data-testid="report-open-production-digest">
                <Link to="/reports/production-stage-digest">Open Report <ArrowRight size={14} className="ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Estimated Delivery report tile. */}
      {canSeeEstimatedDelivery && (
      <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="report-card-estimated-delivery">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 text-amber-700">
            <CalendarClock size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-[var(--ink-navy)]">Estimated Delivery Report</div>
              <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Calendar</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Every active order form grouped by its expected delivery date — Daily, Weekly and Monthly views, Customer or Goldsmith-wise, with overdue roll-up and live stage badges. Click any order to open it.
            </div>
            <div className="flex gap-2 mt-4">
              <Button asChild className="luxury-button" data-testid="report-open-estimated-delivery">
                <Link to="/reports/estimated-delivery">Open Report <ArrowRight size={14} className="ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
      )}

      {visibleReports.map((r) => (
        <Card key={r.id} className="p-5" data-testid={r.testid}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: r.palette, color: r.icon_color }}>
              <r.icon size={18} />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[var(--ink-navy)]">{r.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>
              {r.printable ? (
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setPdfDlg({ open: true, report: r, kind: 'pdf' })} data-testid={`report-pdf-${r.id}`}>
                    <FileText size={14} className="mr-1.5" /> Export PDF
                  </Button>
                  {/* Wave 3.99aa — Mountings card has no Excel export;
                      hide the button when the report doesn't offer one. */}
                  {r.excel_path && (
                    <Button variant="outline" onClick={() => setPdfDlg({ open: true, report: r, kind: 'excel' })} data-testid={`report-excel-${r.id}`}>
                      <FileSpreadsheet size={14} className="mr-1.5" /> Export Excel
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-slate-400 italic mt-4" data-testid={`report-view-only-${r.id}`}>
                  View-only access. Ask your admin for print rights to export.
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}
      <Card className="p-5 bg-[var(--paper-warm)] border-dashed">
        <div className="text-sm text-slate-600">More reports coming soon:</div>
        <ul className="mt-2 text-xs text-slate-500 list-disc pl-5 space-y-1">
          <li>Gold ledger statement (PDF/Excel)</li>
          <li>Vendor purchase summary</li>
          <li>Goldsmith order summary</li>
          <li>Period-wise opening/closing stock</li>
        </ul>
      </Card>

      {pdfDlg.report && (
        <PdfDownloadDialog
          open={pdfDlg.open}
          onOpenChange={(o) => setPdfDlg({ ...pdfDlg, open: o })}
          title={pdfDlg.kind === 'excel' ? `Export ${pdfDlg.report.title} (Excel)` : `Export ${pdfDlg.report.title} (PDF)`}
          description="Choose whether to include the company logo and name in the export header."
          companyId={companyId}
          endpoint={pdfDlg.kind === 'excel' ? pdfDlg.report.excel_path : pdfDlg.report.pdf_path}
          filename={pdfDlg.kind === 'excel' ? pdfDlg.report.excel_name : pdfDlg.report.pdf_name}
          extraParams={{
            company_id: companyId,
            ...(pdfDlg.report.as_of_date ? { as_of_date: pdfDlg.report.as_of_date } : {}),
          }}
          mode="download"
        />
      )}
    </div>
  );
}
